import { useMemo, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Handshake,
  Mail,
  MessageSquare,
  Phone,
  User,
  Building,
  Pencil,
  Trash2,
  Loader2,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { CustomerForm, REFERRER_UNSET, SOURCE_UNSET, type CustomerFormValues } from '@/features/customers/customer-form'
import { LEAD_SOURCE_LABELS, type LeadSource } from '@/types/customers'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import {
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUS_VARIANT,
  customerDisplayName,
} from '@/features/customers/customer-utils'
import { useCustomer, useUpdateCustomer, useDeleteCustomer } from '@/features/customers/hooks'
import { useDeals } from '@/features/customers/deal-hooks'
import { MessagesSidebar } from '@/features/messaging/components/messages-sidebar'
import { useConsignments } from '@/features/consignment/hooks'
import { formatCurrency } from '@/lib/format'
import { requireFlow } from '@/features/auth/guards'
import { BoatWantsCard } from '@/features/customers/components/boat-wants-card'
import {
  boatName,
  CONSIGNMENT_STATUS_LABELS,
  CONSIGNMENT_STATUS_VARIANT,
} from '@/types/consignment'
import { STAGE_DOT, STAGE_LABELS } from '@/types/deal'

export const Route = createFileRoute('/_authenticated/sales/$customerId')({
  beforeLoad: requireFlow('sales'),
  component: CustomerDetailPage,
})

function CustomerDetailPage() {
  const { customerId } = Route.useParams()
  const { data: customer, isLoading, isError } = useCustomer(customerId)
  const navigate = useNavigate()
  const updateMutation = useUpdateCustomer()
  const deleteMutation = useDeleteCustomer()
  const { data: consignedBoats = [], isLoading: consignmentsLoading } = useConsignments({
    consignor_id: customerId,
  })
  const { data: allDeals = [], isLoading: dealsLoading } = useDeals()
  const customerDeals = useMemo(
    () => allDeals.filter((d) => d.customer_id === customerId),
    [allDeals, customerId],
  )
  const [editOpen, setEditOpen] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)

  const { guardOpenChange: guardEditOpen, discardGuard: editDirtyGuard } = useDirtyClose(
    editOpen,
    setEditOpen,
    formDirty,
  )

  const handleUpdate = (values: CustomerFormValues) => {
    updateMutation.mutate({ id: customerId, body: values }, { onSuccess: () => setEditOpen(false) })
  }

  const handleDelete = () => {
    deleteMutation.mutate(customerId, {
      onSuccess: () => navigate({ to: '/sales' }),
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (isError || !customer) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive">Customer not found.</p>
        <Link to="/sales" search={{ tab: 'customers' }}>
          <Button variant="outline">Back to Customers</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/sales" search={{ tab: 'customers' }}>
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Customers
        </Button>
      </Link>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="flex flex-wrap items-center gap-3 break-words text-3xl font-bold tracking-tight">
            {customerDisplayName(customer)}
            <Badge variant={CUSTOMER_STATUS_VARIANT[customer.status]}>
              {CUSTOMER_STATUS_LABELS[customer.status]}
            </Badge>
          </h1>
          <p className="text-muted-foreground">
            Sales Rep: {customer.assigned_sales_rep || '—'} · Source:{' '}
            {customer.lead_source
              ? (LEAD_SOURCE_LABELS[customer.lead_source as LeadSource] ?? customer.lead_source)
              : 'Unknown'}
            {customer.referred_by ? ' (referred)' : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setMessageOpen(true)}>
            <MessageSquare className="mr-2 h-4 w-4" /> Messages
          </Button>
          <MessagesSidebar
            open={messageOpen}
            onOpenChange={setMessageOpen}
            customerId={customerId}
            customerName={customerDisplayName(customer)}
          />
          <Dialog open={editOpen} onOpenChange={guardEditOpen}>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Customer</DialogTitle>
                <DialogDescription>Update this customer's details.</DialogDescription>
              </DialogHeader>
              <CustomerForm
                onDirtyChange={setFormDirty}
                customerId={customerId}
                defaultValues={{
                  first_name: customer.first_name,
                  last_name: customer.last_name,
                  company_name: customer.company_name,
                  contact_email: customer.contact_email,
                  phone: customer.phone,
                  assigned_sales_rep: customer.assigned_sales_rep ?? '',
                  status: customer.status,
                  lead_source: customer.lead_source || SOURCE_UNSET,
                  referred_by: customer.referred_by || REFERRER_UNSET,
                }}
                onSubmit={handleUpdate}
                isPending={updateMutation.isPending}
                submitLabel="Save Changes"
              />
            </DialogContent>
            {editDirtyGuard}
          </Dialog>
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete customer?</DialogTitle>
                <DialogDescription>
                  This permanently deletes {customerDisplayName(customer)}. This action cannot be
                  undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Contact Details</CardTitle>
            <CardDescription>Contact information for this customer.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {customer.company_name && (
              <div className="flex items-center gap-4">
                <Building className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{customer.company_name}</span>
              </div>
            )}
            <div className="flex items-center gap-4">
              <User className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{customerDisplayName(customer)}</span>
            </div>
            <div className="flex items-center gap-4">
              <Mail className="h-5 w-5 shrink-0" />
              <span className="break-all font-medium">{customer.contact_email || '—'}</span>
            </div>
            <div className="flex items-center gap-4">
              <Phone className="h-5 w-5" />
              <span className="font-medium">{customer.phone || '—'}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Consigned Boats</CardTitle>
            <CardDescription>
              Boats this customer has consigned to the marina for sale.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {consignmentsLoading ? (
              <Skeleton className="h-[80px] w-full" />
            ) : consignedBoats.length === 0 ? (
              <div className="h-[80px] flex items-center justify-center text-muted-foreground">
                No consigned boats.
              </div>
            ) : (
              consignedBoats.map((c) => (
                <div
                  key={c.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                >
                  <Link
                    to="/inventory/$boatId"
                    params={{ boatId: c.boat_id }}
                    className="font-medium hover:underline"
                  >
                    {boatName(c)}
                  </Link>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {formatCurrency(c.asking_price)} · {c.commission_rate}%
                    </span>
                    <Badge variant="outline" className={CONSIGNMENT_STATUS_VARIANT[c.status]}>
                      {CONSIGNMENT_STATUS_LABELS[c.status]}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <BoatWantsCard
        customerId={customerId}
        customerFirstName={customer.first_name}
        customerPhone={customer.phone}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Handshake className="h-4 w-4" /> Deals
          </CardTitle>
          <CardDescription>This customer's deals and their current pipeline stage.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {dealsLoading ? (
            <Skeleton className="h-[80px] w-full" />
          ) : customerDeals.length === 0 ? (
            <div className="flex h-[80px] items-center justify-center text-muted-foreground">
              No deals yet.
            </div>
          ) : (
            customerDeals.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <Link
                    to="/sales/deals/$dealId"
                    params={{ dealId: d.id }}
                    className="font-medium hover:underline"
                  >
                    {d.title}
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(d.value)}
                    {d.expected_close
                      ? ` · closes ${new Date(d.expected_close).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}`
                      : ''}
                  </p>
                </div>
                <Badge variant="outline" className="gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${STAGE_DOT[d.stage]}`} />
                  {STAGE_LABELS[d.stage]}
                </Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  )
}
